import { ComponentType, FC, ReactNode, createContext, useContext, useEffect, useMemo } from 'react';

import { AdhocMigration } from '../services/AdhocMigration';
import { ContentEditorService } from '../services/ContentEditorService';
import { EpicIdMigration } from '../services/adhoc-migrations/EpicIdMigration';
import { GuideMissingNodeIdsMigration } from '../services/adhoc-migrations/GuideMissingNodeIdsMigration';
import { MissingSectionsMigration } from '../services/adhoc-migrations/MissingSectionsMigration';
import { TroubleshootingArrayMigration } from '../services/adhoc-migrations/TroubleshootingArrayMigration';
import { AppContextData } from './AppContext';
import { useAutoSavingService } from './AutoSavingServiceContext';
import { useContentValidator } from './ContentValidatorContext';
import { useMplServiceClient } from './MplServiceClientContext';
import { usePendingFlushCoordinator } from './PendingFlushCoordinatorContext';
import { useUserService } from './UserServiceContext';

const ContentEditorServiceContext = createContext<ContentEditorService | undefined>(undefined);

export { ContentEditorServiceContext };

export function ContentEditorServiceProvider({
  children,
  appContext,
}: {
  children: ReactNode;
  appContext?: AppContextData;
}) {
  const mpl = useMplServiceClient();
  const autoSaving = useAutoSavingService();
  const flushCoordinator = usePendingFlushCoordinator();
  const userService = useUserService();
  const contentValidator = useContentValidator();
  const migrations = useMemo<readonly AdhocMigration[]>(
    () => [
      new EpicIdMigration(),
      new TroubleshootingArrayMigration(),
      new MissingSectionsMigration(mpl),
      new GuideMissingNodeIdsMigration(),
    ],
    [mpl],
  );
  const service = useMemo(
    () =>
      new ContentEditorService(
        mpl,
        autoSaving,
        flushCoordinator,
        userService,
        contentValidator,
        migrations,
      ),
    [mpl, autoSaving, flushCoordinator, userService, contentValidator, migrations],
  );

  // Keep the service's validation entities current with the app context
  const entities = appContext?.entities;
  useEffect(() => {
    service.setValidationEntities(entities ?? {});
  }, [service, entities]);

  return (
    <ContentEditorServiceContext.Provider value={service}>
      {children}
    </ContentEditorServiceContext.Provider>
  );
}

export function useContentEditorService(): ContentEditorService {
  const context = useContext(ContentEditorServiceContext);
  if (!context) {
    throw new Error('useContentEditorService must be used within ContentEditorServiceProvider');
  }
  return context;
}

/**
 * Prop shape injected by `withContentEditorService`.
 */
interface WithContentEditorServiceProps {
  readonly contentEditorService: ContentEditorService;
}

/**
 * HOC that injects the `ContentEditorService` as a `contentEditorService` prop.
 *
 * @deprecated This is a workaround for legacy class components that need to
 * consume multiple React contexts (class components natively support only
 * one via `static contextType`). In new code, use the `useContentEditorService`
 * hook directly from a functional component or view model instead.
 */
export function withContentEditorService<P extends WithContentEditorServiceProps>(
  Component: ComponentType<P>,
): FC<Omit<P, keyof WithContentEditorServiceProps>> {
  return function WithContentEditorService(props: Omit<P, keyof WithContentEditorServiceProps>) {
    const contentEditorService = useContentEditorService();
    return <Component {...(props as unknown as P)} contentEditorService={contentEditorService} />;
  };
}
